import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import webApiAdmin from '@/lib/api-common/webApiAdmin';

export interface NotificationDetail {
  userId: string;
  userTitle: string;
  campaignId: string;
}

export interface NotificationItem {
  id: string;
  _id?: string;
  kind: 'message' | 'video' | 'bounced_message' | 'advertiser_action';
  title: string;
  active: boolean;
  details: NotificationDetail;
}

// The new /admin/notifications/load returns raw Mongo docs with `_id` (no
// `id`). Normalize so downstream consumers (mark-checked ids, filters) can
// keep reading `notification.id` regardless of source.
function normalizeNotification(raw: NotificationItem & { _id?: string }): NotificationItem {
  return { ...raw, id: raw.id ?? raw._id ?? '' };
}

interface NotificationsState {
  items: NotificationItem[];
  isLoading: boolean;
}

const initialState: NotificationsState = {
  items: [],
  isLoading: false,
};

/**
 * Load active message notifications for the header bell.
 * - Legacy: GET /api3/notifications/load
 * - New:    GET /admin/notifications/load (success envelope → unwrapped to data)
 * Silent on failure (no alert) — this polls on an interval.
 */
export const loadNotifications = createAsyncThunk<NotificationItem[]>(
  'notifications/load',
  async () => {
    const data = await webApiAdmin.getSilent<NotificationItem[]>('admin/notifications/load');
    return Array.isArray(data) ? data.map(normalizeNotification) : [];
  },
);

/**
 * Mark notifications as checked (deactivate).
 * - Legacy: POST /api3/notifications/markChecked
 * - New:    POST /admin/notifications/mark-checked
 */
export const markNotificationsChecked = createAsyncThunk<void, string[]>(
  'notifications/markChecked',
  async (ids) => {
    await webApiAdmin.post('admin/notifications/mark-checked', { ids });
  },
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(loadNotifications.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(markNotificationsChecked.fulfilled, (state, action) => {
        const checkedIds = action.meta.arg;
        state.items = state.items.filter(
          (n) => !checkedIds.includes(n.id ?? n._id ?? ''),
        );
      });
  },
});

export const { clearNotifications } = notificationsSlice.actions;
export default notificationsSlice.reducer;
